import React, { useContext, useEffect, useState } from 'react';
import "primereact/resources/themes/mira/theme.css";
import './App.css';
import "primereact/resources/primereact.min.css";
import '/node_modules/primeflex/primeflex.css';
import Modal from './Shared/Modal';
import PrivacyPolicy from "./Pages/PrivacyPolicy";
import { AppRouter } from "./router/AppRouter";
import Navbar from './Shared/Navbar';
import Login from './Pages/Auth/Login';
import NoAccess from './Pages/Auth/NoAccess';
import { useTranslation } from 'react-i18next';
import { KeycloakContext } from "./context/KeycloakContext";
import Footer from "./Shared/Footer";
import { privacyStatus } from "./helpers/ApiRequests";
import { useNavigate ,useLocation} from "react-router-dom";

// Import the cookie consent banner component
import CookieConsent, { Cookies } from "react-cookie-consent";

import HeadsetControlBubble from "./Shared/HeadsetControlBubble";

interface ContextType {
  kc: any;
  appInsights: any; // This should be conditionally enabled based on cookie consent
  theme: string;
}

const Frontend_Version= "0.19.1";

const App: React.FC = () => {
  const { kc, appInsights, theme } = useContext<ContextType>(KeycloakContext);
  console.log("theme:",theme);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [hasAccessRole, setHasAccessRole] = useState<boolean>(false);
  const [language, setLanguage] = useState<string>('es'); // ES Default language
  const [backendVersion, setBackendVersion] = useState<string>('');
  const [backendDeploymentSlot, setBackendDeploymentSlot] = useState<string>('');

  const [logoUrl, setLogoUrl] = useState<string>(''); // state to hold the logo image

  const [showModalPrivacy, setShowModalPrivacy] = useState<boolean>(false);

  // State to track if user allowed analytics
  const [allowAnalytics, setAllowAnalytics] = useState<boolean>(() => {
    // On first load, check cookie
    const consentValue = Cookies.get('CookieConsent');
    // The `react-cookie-consent` sets 'CookieConsent' to 'true' if accepted.
    // We need more nuanced handling:
    // Let's say we use a different cookie name for analytics: 'analyticsConsent'
    const analyticsConsent = Cookies.get('analyticsConsent');
    return analyticsConsent === 'true';
  });

  // State to control the visibility of the cookie banner,
  // allowing user to reopen it by clearing their previous choice.
  const [showCookieBanner, setShowCookieBanner] = useState<boolean>(() => {
    // If user has previously chosen, don't show by default
    const analyticsConsent = Cookies.get('analyticsConsent');
    return !analyticsConsent;
  });

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation(); // <- to see which route the user is on

  // Decide if the route is one of the public routes
  // For example, if the path starts with "/ShowMeasurement"
  const isShowMeasurementRoute = location.pathname.toLowerCase().startsWith("/showmeasurement");

  // Map each theme to the tab title
  const THEME_TITLES: Record<string, string> = {
    "mimetika-web": "Mimetika",
    "mimetika-web-ov": "WIDRME",
    "mimetika-web-wl1": "Gaze Analysis",
  };

  // Update the browser tab title when theme changes
  useEffect(() => {
    const base = THEME_TITLES[theme] ?? "Mimetika";
    document.title = base;
  }, [theme]);

  useEffect(() => {
    changeFavicon(theme);
  }, [theme]);

  // Effect to sync language state with i18n language
  useEffect(() => {

    setLanguage(i18n.language);
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    // Cleanup
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);


  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/version`, {  })
    .then(response => response.json())
    .then(data => {
      setBackendVersion(data.version);
      setBackendDeploymentSlot(data.deploymentSlot);
      console.log('Backend version:', data.version);
      console.log('Backend Deployment Slot:', data.deploymentSlot);
      console.log('Frontend version:', Frontend_Version);
      console.log('Frontend Deployment Slot:', process.env.REACT_APP_DEPLOYMENT_SLOT);
    })
    .catch(error => {
      console.error('Error fetching backend version:', error);
    });
  }, []);

  useEffect(() => {
    async function onAuthSuccess() {
      setIsAuthenticated(true);
      checkUserRole();

      // Define the allowed languages.
      const allowedLanguages = ['es', 'de', 'en', 'fr', 'pt', 'it'];

      // Get the locale from the Keycloak token; default to 'es' if not provided.
      let tokenLocale = kc.tokenParsed.locale || 'es';

      // Normalize to lowercase in case the token provides uppercase values.
      tokenLocale = tokenLocale.toLowerCase();

      // Check if the tokenLocale is allowed; if not, default to 'es'.
      const userLocale = allowedLanguages.includes(tokenLocale) ? tokenLocale : 'es';

      // Change the language in i18n.
      await i18n.changeLanguage(userLocale);

      // Only fetch user details and track events if user allowed analytics
      fetchUserDetails();
      getPrivacyStatus();

      if (allowAnalytics && appInsights) {
        appInsights.trackEvent({ name: "Login Successful" });
      }

      navigate("/");
    }

    kc.onAuthSuccess = onAuthSuccess;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowAnalytics]);

  const checkUserRole = () => {
    const hasRole = kc.hasResourceRole('access');
    setHasAccessRole(hasRole);
    if (!hasRole) {
      console.error('No License');
    }
  };

  useEffect(() => {
    const refreshToken = async () => {
        if (kc.authenticated && kc.isTokenExpired(30)) {
            try {
                await kc.updateToken(30);
                console.log('Token refreshed');
            } catch (error) {
                console.error('Failed to refresh the token, logging out', error);
                kc.logout({ redirectUri: window.location.href });
            }
        }
    };

    if (kc.authenticated) {
        // Call refreshToken immediately on component mount
        refreshToken();

        // Set up interval to refresh token periodically
        const interval = setInterval(async () => {
            try {
                await refreshToken();
            } catch (error) {
                console.error('Error during token refresh:', error);
            }
        }, 1 * 60 * 1000); // Refresh every minute

        return () => clearInterval(interval);
    }
}, [kc.authenticated]);



  const fetchUserDetails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user-details`, {
        headers: { Authorization: `Bearer ${kc.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.logo) {
          setLogoUrl(`data:image/png;base64,${data.logo}`);
        }
        if (allowAnalytics && appInsights) {
          appInsights.trackEvent({ name: "Fetch User Details Success" });
        }
      } else {
        throw new Error('Response not OK');
      }
    } catch (error: unknown) {
      console.error('Error fetching user details:', error);
      if (allowAnalytics && appInsights && error instanceof Error) {
        appInsights.trackException({
          exception: new Error('Fetch User Details Failed'),
          properties: { error: error.message }
        });
      }
    }
  };

  const getPrivacyStatus = async () => {
    const status = await privacyStatus(kc);
    console.log( 'getPrivacyStatussss', status)
    if (status) {
      // User already agreed to the privacy terms of the webapp after login
      setShowModalPrivacy(false);
    } else {
      // Show privacy modal (the "contract") after login
      setShowModalPrivacy(true);
    }
  };

  const logout = () => {
    const redirectUriLogout = window.location.href;
    if (allowAnalytics && appInsights) {
      appInsights.trackEvent({ name: "Logout" });
    }
    kc.logout({ redirectUri: redirectUriLogout });
  };

  // const handleLiveView = async () => {
  //   try {
  //     const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-casting-url`, {
  //       headers: { Authorization: `Bearer ${kc.token}` }
  //     });
  //     const data = await response.json();
  //     if (response.ok && data.castingURL) {
  //       window.open(data.castingURL, '_blank');
  //     } else {
  //       console.log('No casting URL provided or an error occurred:', data.message);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching casting URL:', error);
  //   }
  // };

  // Function to reopen the cookie banner:
  const reopenCookieBanner = () => {
    // Remove previous decisions
    Cookies.remove('analyticsConsent');
    Cookies.remove('CookieConsent');
    setAllowAnalytics(false);
    setShowCookieBanner(true);
  };


  const getBGClassName = (theme: string) => {
    switch (theme) {
      case 'mimetika-web':
        return 'bg-mimetika bg-blue-dark';
      case 'mimetika-web-wl1':
        return 'bg-black';
      case 'mimetika-web-ov':
        return 'bg-green-dark';
      default:
        return 'bg-default';
    }
  };


  const changeFavicon = ( theme: string ) => {
    const isMimetikaOV = theme === 'mimetika-web-ov';
    const isMimetika = theme === 'mimetika-web';
    let url = ''
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");

    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }

    if (isMimetika) {
      url = 'favicon.ico' + '?v=' + Date.now();
      link.href = url;
    } else if (isMimetikaOV) {
      url = '/favicons/favicon-op.ico' + '?v=' + Date.now();
      link.href = url;
    } else {
      url = '/favicons/favicon-ga.ico' + '?v=' + Date.now();
      link.href = url;
    }


  };

  useEffect(() => {
    changeFavicon( theme );
  }, []);


/**
   * -----------
   * RENDER LOGIC
   * -----------
   * If the user is going to /ShowMeasurement or /ShowMeasurement/:uuid,
   * we do NOT force them to be authenticated. We simply return the app router
   * so that those routes can render publicly.
   * Otherwise, we do the normal logic:
   *  - If not authenticated -> show <Login />
   *  - If has no 'access' role -> show <NoAccess />
   *  - Else -> show normal app with Navbar, etc.
   */

  // 1) If user is on /ShowMeasurement route, skip the Keycloak checks
  if (isShowMeasurementRoute) {
    return (
      <div className="App">
        {showCookieBanner && (
          <div data-cy="cookie-banner">
            <CookieConsent
              location="bottom"
              buttonText={t("Cookies.AllowAllCookies")}
              declineButtonText={t("Cookies.OnlyNecessary")}
              enableDeclineButton
              cookieName="CookieConsent"
              style={{ background: "#2B373B" }}
              buttonStyle={{ background: "#4CAF50", color: "#fff", fontSize: "13px" }}
              declineButtonStyle={{ background: "#f44336", color: "#fff", fontSize: "13px" }}
              expires={365}
              onAccept={() => {
                Cookies.set('analyticsConsent', 'true', { expires: 365 });
                setAllowAnalytics(true);
                setShowCookieBanner(false);
                if (appInsights) {
                  appInsights.trackEvent({ name: "UserAllowedAllCookies" });
                }
              }}
              onDecline={() => {
                Cookies.set('analyticsConsent', 'false', { expires: 365 });
                setAllowAnalytics(false);
                setShowCookieBanner(false);
              }}
            >
              <strong>{t('Cookies.Title')}</strong>
              <br /><br />
              {t('Cookies.Text1')}
              <br /><br />
              {t('Cookies.Text2')}
              <br /><br />
              {t('Cookies.Text3')}{" "}
              <a href="/privacy-policy" style={{ color: "#fff", textDecoration: "underline" }}>
                {t('Privacy Policy')}
              </a>.
              {" "}{t('Cookies.Text4')}
            </CookieConsent>
          </div>
        )}

        {/* No Keycloak checks here: just show the routes (which includes ShowMeasurement routes) */}
        <AppRouter />
      </div>
    );
  }

  // 2) For all other routes, do the normal login/role checks
  return (
    <div className="App">
      {/* Cookie Banner shown before login. This will appear at initial load if no choice was made. */}
      {!Cookies.get('analyticsConsent') && (
        <div data-cy="cookie-banner">
          <CookieConsent
            location="bottom"
            buttonText={t("Cookies.AllowAllCookies")}
            declineButtonText={t("Cookies.OnlyNecessary")}
            enableDeclineButton
            cookieName="CookieConsent"
            style={{ background: "#2B373B" }}
            buttonStyle={{ background: "#4CAF50", color: "#fff", fontSize: "13px" }}
            declineButtonStyle={{ background: "#f44336", color: "#fff", fontSize: "13px" }}
            expires={365}
            onAccept={() => {
              // User accepted all cookies including analytics
              Cookies.set('analyticsConsent', 'true', { expires: 365 });
              setAllowAnalytics(true);
              setShowCookieBanner(false);
              if (appInsights) {
                appInsights.trackEvent({ name: "UserAllowedAllCookies" });
              }
            }}
            onDecline={() => {
              // User only allows necessary cookies, no analytics
              Cookies.set('analyticsConsent', 'false', { expires: 365 });
              setAllowAnalytics(false);
              setShowCookieBanner(false);

            }}
          >
            <strong>{t('Cookies.Title')}</strong><br /><br />
            {t('Cookies.Text1')}
            <br /><br />
            {t('Cookies.Text2')}
            <br /><br />
            {t('Cookies.Text3')} <a href="/privacy-policy" style={{ color: "#fff", textDecoration: "underline" }}>{t('Privacy Policy')}</a>.
            {t('Cookies.Text4')}
          </CookieConsent>
        </div>

      )}

      {!isAuthenticated ? (
        <Login language={language}/>
      ) : (
        hasAccessRole ? (
          //<div className={'bg-mimetika min-h-screen bg-blue-dark'}>
          <div  className={`min-h-screen ${getBGClassName(theme)}`}>
            <Navbar appInsights={allowAnalytics ? appInsights : null} logout={logout} theme={theme}/>
            <AppRouter />

            { showModalPrivacy && (
                  <Modal themeClass={theme}>
                    <PrivacyPolicy onCheckPrivacy={setShowModalPrivacy}/>
                  </Modal>
            )}
            {isAuthenticated && hasAccessRole && !isShowMeasurementRoute && (
              <HeadsetControlBubble apiBaseUrl={process.env.REACT_APP_API_BASE_URL as string} kc={kc} />
            )}
            <Footer onManageCookies={reopenCookieBanner} theme={theme}/>
          </div>
        ) : (
          <div className="container no-access">
            <NoAccess logout={logout}/>
          </div>
        )
      )}
    </div>
  );
};

export default App;
